/**
 * AuthLayout — 로그인 전(pre-login) 화면 공용 골격
 *
 * D-07 §21.5 **L9 인증 2분할**(좌 브랜드 / 우 폼, Shell 밖).
 * L9·L10은 Shell 밖이라 `Page`·`PageScroll`·`PageHeader` 프리미티브를 쓰지 않는다(§21.5).
 * D-09 에서 `SplitPane` 의 ui-core 편입이 부결됐으므로, 2분할 공용화는 이 앱 레벨
 * 레이아웃이 담당한다(위치는 D-02 디렉터리 규정 `app/layouts/`).
 *
 * 이 레이아웃이 소유하는 것 — 화면은 폼 본문만 갖는다:
 *   · 2분할 골격과 `md:` 브레이크포인트
 *   · 좌측 브랜드 패널(폭 44%)
 *   · 우측 패널 패딩 (§21.1 Page Padding 16 / 32)
 *   · 폼 컬럼 max-width (§21.3 `sm` = 480 "좁은 폼·인증")
 *   · 타이틀 마크업과 간격 (h1 `heading-lg` + 서브 `body-sm`, §21.1 Section Gap)
 */
import React from 'react';

/** 브랜드 패널 하단 표기 — 화면마다 갈리지 않게 한 곳에서 관리한다. */
const BRAND_VERSION = 'v1.0 · 2026';

/**
 * 폼 컬럼 폭 — D-07 §21.3 컨테이너 스케일(sm 480) 밖의 값이다.
 * 인증은 §21.5 **L9** 이고 L9 행의 폭 칸은 표준에 `—`(미규정)이다("골격만 이 표를 따른다").
 * 즉 §21.3 이 L9 의 폭을 강제하지 않으므로, 제품 결정으로 380 을 쓴다.
 * 스케일에 인증용 폭이 생기면 그 토큰으로 교체한다.
 */
const FORM_COLUMN_MAX_WIDTH = 380; // dq-ignore D-07 §21.5 L9 폭 미규정

// ── 좌측 브랜딩 패널 ──────────────────────────────────────────────────────

interface BrandPanelProps {
  kicker: string;
}

export function AuthBrandPanel({ kicker }: BrandPanelProps) {
  return (
    // 좌 44% : 우 56% — D-07 §21.5 L9 는 2분할 비율을 규정하지 않는다(폭 칸이 `—`).
    // 스케일 값이 아니라 분할 비율이므로 토큰 대상이 아니다.
    // aside 랜드마크: 브랜드 패널은 폼의 보조 콘텐츠라 main 밖의 랜드마크로 감싼다 (D-07 §19)
    <aside
      className="relative flex w-full shrink-0 flex-col overflow-hidden bg-primary-700
        px-05 pb-07 pt-07
        md:w-[44%] md:min-h-screen md:px-14 md:pb-09 md:pt-09"
    >
      {/* 브랜드 워드마크 */}
      <div className="flex items-center gap-2">
        <span className="text-body-sm font-bold uppercase tracking-widest text-white">
          INDIPIA
        </span>
        <span className="bg-white/60" style={{ width: 5, height: 5, borderRadius: '50%', flexShrink: 0, display: 'inline-block' }} />
        <span className="text-caption font-semibold uppercase tracking-widest text-[var(--alpha-white-800)]">
          Pro
        </span>
      </div>

      {/* 헤드라인 */}
      <div className="flex flex-1 flex-col justify-center pb-8">
        <p className="mb-05 text-caption font-semibold uppercase tracking-widest text-[var(--alpha-white-800)]">
          {kicker}
        </p>
        <h2
          className="text-display-sm font-bold text-white"
          style={{ wordBreak: 'keep-all' }}
        >
          오늘부터<br />
          나만의 스튜디오가<br />
          생깁니다.
        </h2>
        <div className="my-06 bg-white/60" style={{ width: 40, height: 2, borderRadius: 'var(--radius-xs)' }} />
        <p className="text-body-sm text-[var(--alpha-white-800)]" style={{ wordBreak: 'keep-all' }}>
          사장님과 현장을 더 스마트하게.<br />
          인피디아가 함께합니다.
        </p>
      </div>

      {/* 버전 */}
      <p className="text-caption text-[var(--alpha-white-800)]" style={{ marginTop: 'auto' }}>
        {BRAND_VERSION}
      </p>
    </aside>
  );
}

// ── 2분할 레이아웃 ────────────────────────────────────────────────────────

export interface AuthLayoutProps {
  /** 좌측 브랜드 패널 키커 (예: "Member Login") */
  kicker: string;
  /** 우측 컬럼 최상단 우측 슬롯 — 반대편 화면으로 가는 링크 */
  topRight?: React.ReactNode;
  /** 페이지 제목 — 항상 h1 으로 렌더한다 (화면당 h1 정확히 1개) */
  title: React.ReactNode;
  /** 제목 아래 안내 문구 */
  subtitle?: React.ReactNode;
  /** 제목·서브 정렬. 역할 선택 화면이 center */
  align?: 'start' | 'center';
  /** 제목 위 알림 슬롯 (Alert) */
  banner?: React.ReactNode;
  /** 폼 하단 액션 영역 — 버튼 1개면 전폭, 2개면 [이전][다음] 행 */
  actions?: React.ReactNode;
  children: React.ReactNode;
}

export function AuthLayout({
  kicker,
  topRight,
  title,
  subtitle,
  align = 'start',
  banner,
  actions,
  children,
}: AuthLayoutProps) {
  return (
    <div className="flex min-h-screen flex-col overflow-hidden bg-surface md:flex-row">
      <AuthBrandPanel kicker={kicker} />

      {/* 우측 폼 패널 — 패딩은 §21.1 Page Padding (Mobile 16 / Desktop 32)
          main 랜드마크: 인증 화면의 콘텐츠 소유자라 이 요소가 유일한 main 이다 (D-07 §19) */}
      <main className="flex flex-1 flex-col bg-surface-raised p-05 md:p-page">
        {topRight && <div className="flex justify-end">{topRight}</div>}

        <div className="flex flex-1 flex-col md:items-center md:justify-center">
          {/* 폼 컬럼 — §7.3 대로 width 가 아니라 max-width 로만 잡는다 */}
          <div
            className={`mx-auto w-full ${align === 'center' ? 'text-center' : ''}`}
            style={{ maxWidth: FORM_COLUMN_MAX_WIDTH }}
          >
            {banner && <div className="mb-06">{banner}</div>}

            <h1 className="text-heading-lg font-bold text-text">{title}</h1>
            {subtitle && (
              <p className="mt-03 text-body-sm text-text-secondary" style={{ wordBreak: 'keep-all' }}>
                {subtitle}
              </p>
            )}

            {/* 헤더 하단 여백 — D-09 §15.43 PageHeader 준용(gap-lg 24) */}
            <div className={`${align === 'center' ? 'text-left' : ''} mt-06`}>{children}</div>

            {actions && <div className="mt-06">{actions}</div>}
          </div>
        </div>
      </main>
    </div>
  );
}
